import { useState, useCallback } from 'react'
import {
  createFolder as createFolderApi,
  updateFolder as updateFolderApi,
  deleteFolder as deleteFolderApi,
  moveFolder as moveFolderApi,
  moveFileToFolder,
  moveFilesToFolder,
  listFolders
} from '../../../services/api'
import type { TranscriptionFolder } from '../types/folder.types'

interface UseFoldersReturn {
  // Estado do dialog
  showCreateDialog: boolean
  editingFolder: TranscriptionFolder | null
  showMoveDialog: boolean
  movingFolder: TranscriptionFolder | null

  // Actions de dialog
  openCreateDialog: () => void
  closeCreateDialog: () => void
  openEditDialog: (folder: TranscriptionFolder) => void
  closeEditDialog: () => void
  openMoveDialog: (folder?: TranscriptionFolder) => void
  closeMoveDialog: () => void

  // CRUD
  createFolder: (name: string, color: string | null, parentId?: string | null) => Promise<boolean>
  renameFolder: (name: string, color: string | null) => Promise<boolean>
  deleteFolder: (folder: TranscriptionFolder) => Promise<boolean>
  moveFolder: (folderId: string, targetFolderId: string | null) => Promise<boolean>
  moveFiles: (fileIds: string[], targetFolderId: string | null) => Promise<boolean>

  // Toast state
  moveToast: { open: boolean; message: string; folderName?: string; folderColor?: string }
  closeMoveToast: () => void
}

export function useFolders(
  onSuccess?: () => void,
  onError?: (message: string) => void
): UseFoldersReturn {
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingFolder, setEditingFolder] = useState<TranscriptionFolder | null>(null)
  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [movingFolder, setMovingFolder] = useState<TranscriptionFolder | null>(null)

  // Toast state
  const [moveToast, setMoveToast] = useState<{
    open: boolean
    message: string
    folderName?: string
    folderColor?: string
  }>({ open: false, message: '' })

  // Dialog actions
  const openCreateDialog = useCallback(() => setShowCreateDialog(true), [])
  const closeCreateDialog = useCallback(() => setShowCreateDialog(false), [])

  const openEditDialog = useCallback((folder: TranscriptionFolder) => {
    setEditingFolder(folder)
  }, [])
  const closeEditDialog = useCallback(() => setEditingFolder(null), [])

  const openMoveDialog = useCallback((folder?: TranscriptionFolder) => {
    if (folder) setMovingFolder(folder)
    setShowMoveDialog(true)
  }, [])

  const closeMoveDialog = useCallback(() => {
    setShowMoveDialog(false)
    setMovingFolder(null)
  }, [])

  const closeMoveToast = useCallback(() => {
    setMoveToast(prev => ({ ...prev, open: false }))
  }, [])

  // CRUD operations
  const createFolder = useCallback(
    async (name: string, color: string | null, parentId?: string | null): Promise<boolean> => {
      try {
        await createFolderApi({
          name,
          color: color || undefined,
          parentFolderId: parentId || undefined
        })
        setShowCreateDialog(false)
        onSuccess?.()
        return true
      } catch (e: any) {
        onError?.(`Erro ao criar pasta: ${e.message || 'erro desconhecido'}`)
        return false
      }
    },
    [onSuccess, onError]
  )

  const renameFolder = useCallback(
    async (name: string, color: string | null): Promise<boolean> => {
      if (!editingFolder) return false
      try {
        await updateFolderApi(editingFolder.id, {
          name,
          color: color || undefined
        })
        setEditingFolder(null)
        onSuccess?.()
        return true
      } catch (e: any) {
        onError?.(`Erro ao renomear pasta: ${e.message || 'erro desconhecido'}`)
        return false
      }
    },
    [editingFolder, onSuccess, onError]
  )

  const deleteFolder = useCallback(
    async (folder: TranscriptionFolder): Promise<boolean> => {
      try {
        await deleteFolderApi(folder.id)
        onSuccess?.()
        return true
      } catch (e: any) {
        onError?.(`Erro ao excluir pasta: ${e.message || 'erro desconhecido'}`)
        return false
      }
    },
    [onSuccess, onError]
  )

  const moveFolder = useCallback(
    async (folderId: string, targetFolderId: string | null): Promise<boolean> => {
      try {
        await moveFolderApi(folderId, targetFolderId || '')

        // Busca info da pasta destino
        let targetFolder: TranscriptionFolder | undefined
        if (targetFolderId) {
          const allFolders = await listFolders(null)
          targetFolder = allFolders.find((f: TranscriptionFolder) => f.id === targetFolderId)
        }

        setMoveToast({
          open: true,
          message: 'Pasta movida com sucesso',
          folderName: targetFolder?.name || 'Raiz',
          folderColor: targetFolder?.color || undefined
        })

        closeMoveDialog()
        onSuccess?.()
        return true
      } catch (e: any) {
        onError?.(`Erro ao mover pasta: ${e.message || 'erro desconhecido'}`)
        return false
      }
    },
    [closeMoveDialog, onSuccess, onError]
  )

  const moveFiles = useCallback(
    async (fileIds: string[], targetFolderId: string | null): Promise<boolean> => {
      try {
        if (fileIds.length === 1) {
          await moveFileToFolder(fileIds[0], targetFolderId)
        } else {
          await moveFilesToFolder(fileIds, targetFolderId)
        }

        // Busca info da pasta destino
        let targetFolder: TranscriptionFolder | undefined
        if (targetFolderId) {
          const allFolders = await listFolders(null)
          targetFolder = allFolders.find((f: TranscriptionFolder) => f.id === targetFolderId)
        }

        setMoveToast({
          open: true,
          message: `${fileIds.length} arquivo(s) movido(s)`,
          folderName: targetFolder?.name || 'Raiz',
          folderColor: targetFolder?.color || undefined
        })

        closeMoveDialog()
        onSuccess?.()
        return true
      } catch (e: any) {
        onError?.(`Erro ao mover arquivos: ${e.message || 'erro desconhecido'}`)
        return false
      }
    },
    [closeMoveDialog, onSuccess, onError]
  )

  return {
    showCreateDialog,
    editingFolder,
    showMoveDialog,
    movingFolder,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
    openMoveDialog,
    closeMoveDialog,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    moveFiles,
    moveToast,
    closeMoveToast
  }
}

export default useFolders
